/**
 * KeyboardHint - Contextual keyboard shortcut hint
 *
 * Shows a small, subtle hint that fades out after a few seconds.
 * Used to help users discover keyboard shortcuts without cluttering the UI.
 */

import React, { useState, useEffect } from "react";

export interface KeyboardHintProps {
  /** The keyboard shortcut to display */
  shortcut: string;
  /** Optional description */
  description?: string;
  /** Position relative to parent */
  position?: "top" | "bottom" | "left" | "right";
  /** Time in ms before fading (default: 5000) */
  fadeAfter?: number;
  /** Whether to show the hint */
  show?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const KeyboardHint: React.FC<KeyboardHintProps> = ({
  shortcut,
  description,
  position = "bottom",
  fadeAfter = 5000,
  show = true,
  className = "",
}) => {
  const [visible, setVisible] = useState(show);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!show) {
      setVisible(false);
      return;
    }

    setVisible(true);
    setFading(false);

    const fadeTimer = setTimeout(() => {
      setFading(true);
    }, fadeAfter);

    const hideTimer = setTimeout(() => {
      setVisible(false);
    }, fadeAfter + 500); // 500ms fade transition

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [show, fadeAfter]);

  if (!visible) {
    return null;
  }

  const positionClasses = {
    top: "-top-6 left-1/2 -translate-x-1/2",
    bottom: "-bottom-6 left-1/2 -translate-x-1/2",
    left: "top-1/2 -translate-y-1/2 -left-20",
    right: "top-1/2 -translate-y-1/2 -right-20",
  };

  return (
    <div
      className={`
        absolute pointer-events-none z-30
        ${positionClasses[position]}
        transition-opacity duration-500
        ${fading ? "opacity-0" : "opacity-100"}
        ${className}
      `}
    >
      <div className="flex items-center gap-1 px-1.5 py-0.5 bg-bg-2/80 backdrop-blur-xs rounded text-[10px]">
        <kbd className="px-1 py-0.5 bg-surface-2 rounded text-fg font-mono">{shortcut}</kbd>
        {description && <span className="text-fg-muted">{description}</span>}
      </div>
    </div>
  );
};

export default KeyboardHint;
